import React from 'react';
import {
    isLinkedVariation,
    buildLinkedVariationPicker,
    variationNeedsAvailabilityConfirmation,
} from '../../utils/productUtils';
import {
    getFormattedPriceParts,
    getProductPriceSummary,
} from '../../utils/priceUtils';
import VariationAvailabilityNotice from './VariationAvailabilityNotice';

const VariationSelector = React.forwardRef(({
    product,
    selectedVariations,
    setSelectedVariations,
    onVariationChange,
    animateFocus,
    linkedVariationOrderMap,
    userInfo,
    movedLinkedVariationType,
    movedLinkedVariationLabel,
}, ref) => {
    if (!product.variations || product.variations.length === 0) return null;

    const defaultImage = '/images/default.jpg';

    return (
        <div 
            ref={ref}
            className={`variation-selector-wrapper my-3 ${animateFocus ? 'variation-focus-pulse' : ''}`}
        >
            {product.variations.map((v, idx) => {
                const linkedMode = isLinkedVariation(v);
                const pickerOptions = linkedMode
                    ? buildLinkedVariationPicker(
                        product,
                        v,
                        linkedVariationOrderMap?.[v.variationType]
                    ).map((opt) => {
                          const linkedSource = v.options.find((option) => option.linkedProduct === opt.id);
                          const linkedOptionPricingData =
                              linkedSource &&
                              (linkedSource.price !== undefined ||
                                  linkedSource.priceDist !== undefined ||
                                  linkedSource.discount !== undefined ||
                                  linkedSource.appliedDiscountPercentage !== undefined)
                                  ? {
                                        price: linkedSource.price,
                                        priceDist: linkedSource.priceDist,
                                        taxPercentage: linkedSource.taxPercentage ?? product.taxPercentage,
                                        discount: linkedSource.discount,
                                        appliedDiscountPercentage: linkedSource.appliedDiscountPercentage,
                                        discountDateFrom: linkedSource.discountDateFrom,
                                        discountDateTo: linkedSource.discountDateTo,
                                    }
                                  : null;
                          const linkedProductData = opt.id === product._id
                              ? product
                              : (typeof linkedSource?.linkedProduct === 'object'
                                  ? linkedSource.linkedProduct
                                  : linkedOptionPricingData);
                          const linkedPriceValue = linkedProductData
                              ? getProductPriceSummary(linkedProductData, userInfo).finalPrice
                              : null;
                          const linkedPriceParts = linkedPriceValue !== null
                              ? getFormattedPriceParts(linkedPriceValue)
                              : null;
                          return {
                              ...opt,
                              image: opt.id === product._id ? product.image : linkedSource?.image,
                              linkedPriceParts,
                          };
                      })
                    : [];
                const selectedValue = linkedMode
                    ? (selectedVariations[v.variationType]?.linkedProduct || product._id)
                    : (selectedVariations[v.variationType]?.name || '');
                const showAvailabilityNotice = variationNeedsAvailabilityConfirmation(v);
                const options = linkedMode ? pickerOptions : (v.options || []);
                const shouldAnimateLinkedMove = linkedMode && movedLinkedVariationType === v.variationType;

                return (
                    <div key={idx}>
                        <div className={`variation-chip-group mb-2 ${shouldAnimateLinkedMove ? 'linked-move-focus' : ''}`}>
                            <div className="variation-chip-group-label">
                                {v.variationType}:
                            </div>
                            <div className="variation-chip-list" role="radiogroup" aria-label={`Seleccionar ${v.variationType}`}>
                                {options.map((opt, oIdx) => {
                                    const value = linkedMode ? opt.id : opt.name;
                                    const isSelected = value === selectedValue;
                                    const optionLabel = linkedMode ? opt.label : opt.name;
                                    const optionImage = linkedMode ? opt.image : opt.image;
                                    const hasImage = !!optionImage;
                                    const optionInitials = (optionLabel || '')
                                        .split(' ')
                                        .filter(Boolean)
                                        .slice(0, 2)
                                        .map((part) => part[0])
                                        .join('')
                                        .toUpperCase() || '?';

                                    return (
                                        <button
                                            key={linkedMode ? `${opt.id}-${oIdx}` : `${opt.name}-${oIdx}`}
                                            type="button"
                                            className={`variation-chip-button ${hasImage ? '' : 'no-image'} ${isSelected ? 'active' : ''}`}
                                            onClick={() => {
                                                if (linkedMode) {
                                                    const selectedId = opt.id;
                                                    const selectedOption = {
                                                        name: opt.label || product.name,
                                                        linkedProduct: selectedId,
                                                    };
                                                    const newVariations = {
                                                        ...selectedVariations,
                                                        [v.variationType]: selectedOption,
                                                    };
                                                    setSelectedVariations(newVariations);
                                                    if (onVariationChange) {
                                                        onVariationChange(selectedOption, newVariations, {
                                                            variationType: v.variationType,
                                                            isLinkedVariation: true,
                                                            selectedId,
                                                        });
                                                    }
                                                    return;
                                                }

                                                const selectedOption = v.options.find((item) => item.name === opt.name);
                                                const newVariations = {
                                                    ...selectedVariations,
                                                    [v.variationType]: selectedOption,
                                                };
                                                setSelectedVariations(newVariations);
                                                if (onVariationChange) {
                                                    onVariationChange(selectedOption, newVariations, {
                                                        variationType: v.variationType,
                                                        isLinkedVariation: false,
                                                    });
                                                }
                                            }}
                                            role="radio"
                                            aria-checked={isSelected}
                                        >
                                            {hasImage ? (
                                                <img
                                                    src={optionImage}
                                                    alt={optionLabel}
                                                    className="variation-chip-image"
                                                    onError={(e) => {
                                                        e.currentTarget.onerror = null;
                                                        e.currentTarget.src = defaultImage;
                                                    }}
                                                />
                                            ) : (
                                                <span className="variation-chip-fallback" aria-hidden="true">
                                                    {optionInitials}
                                                </span>
                                            )}
                                            <span className="variation-chip-content">
                                                <span className="variation-chip-text">{optionLabel}</span>
                                                {linkedMode && opt.linkedPriceParts && (
                                                    <span className="variation-chip-price">
                                                        ${opt.linkedPriceParts[0]}.{opt.linkedPriceParts[1]}
                                                    </span>
                                                )}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {showAvailabilityNotice && (
                            <VariationAvailabilityNotice
                                product={product}
                                variationType={v.variationType}
                                selectedVariation={selectedVariations[v.variationType]}
                                title={`Confirma ${v.variationType}`}
                                description="Esta variacion no esta enlazada automaticamente. Confirma disponibilidad antes de finalizar tu pedido."
                                cardClassName="variation-availability-card--modal shadow-sm"
                                buttonClassName="mt-1"
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
});

export default VariationSelector;
